import { localized } from "@lit/localize";
import { css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import type { ConnectedDevicesContent, Device } from "../../types/content";
import "../../elements/web-kiosk-home-link";
import { WebKioskPageElement } from "../web-kiosk-page";
import { getSearch } from "../../utilities/navigation.utility";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-devices": WebKioskConnectedDevicesElement;
  }
}

@localized()
@customElement("web-kiosk-connected-devices")
export class WebKioskConnectedDevicesElement extends WebKioskPageElement {
  @state() content: ConnectedDevicesContent | undefined = undefined;

  createDeviceUrl(device: Device): string {
    if (!this.location) {
      return "";
    }

    const payor = this.getPayor();
    const locale = this.getLocale();
    const search = getSearch(this.location).replace("?", "");
    const searchParameters = search === "" ? search : `&${search}`;

    return `/${payor}/${locale}/connectedDevice?device=${device.id}${searchParameters}`;
  }

  renderCards(
    devices: Device[] | undefined
  ): TemplateResult[] | typeof nothing {
    if (!devices || devices.length === 0) {
      return nothing;
    }

    return devices.map((device) => {
      const deviceLink = this.createDeviceUrl(device);

      return html` <a class="device-link" href=${deviceLink}>
        <div class="device-card">
          <div class="img-container">
            <img src=${device.thumbnail} alt=${device.name} />
          </div>
          <div class="text-container">
            <h2>${device.name}</h2>
          </div>
        </div>
      </a>`;
    });
  }

  render(): TemplateResult {
    return html`<section class="page-body">
      <div class="body-inner">
        <web-kiosk-home-link
          link=${this.createRelativeLink("home")}
        ></web-kiosk-home-link>

        <div class="body-content">
          <section id="description">
            <h1>${this.content?.heading}</h1>

            <p>${this.content?.description}</p>
          </section>
          <section id="cards">
            ${this.renderCards(this.content?.devices)}
          </section>
        </div>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
      --web-kiosk-device-card-bg: #f5f5f0;
      --web-kiosk-device-card-img-bg: #fff;
      --web-kiosk-dimension-spacer-xxxs: 8px;
      --web-kiosk-dimension-spacer-m: 32px;
    }

    .page-body {
      height: 100%;
      background: url("assets/connectedDevices/swirl.svg") 50% 80% no-repeat,
        var(--web-kiosk-bg, hotpink);
      background-size: contain;
    }

    .body-inner {
      padding: 48px 56px;
    }

    .home-link {
      color: var(--web-kiosk-link-color, hotpink);
    }

    .home-link img {
      margin-right: var(--web-kiosk-dimension-spacer-xxxs, 8px);
    }

    a.home-link {
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      font-size: 33px;
      font-weight: 700;
      line-height: 40px;
      color: var(--web-kiosk-link-color, hotpink);
    }

    a.device-link {
      text-decoration: none;
      display: flex;
      flex: 0 0 423px;
      flex-direction: column;
      gap: 10px;
      align-items: center;
      background: var(--web-kiosk-device-card-bg, hotpink);
      border-radius: 2rem;
      border: none;
      overflow: hidden;
      text-align: center;
      padding-bottom: 68px;
    }

    #description {
      display: flex;
      gap: 80px;
    }

    #description h1 {
      flex: 1 0 39%;
      margin: 52px 0 40px;
      font-size: 64px;
      font-weight: 700;
      line-height: 72px;
      color: var(--web-kiosk-text-color, hotpink);
    }

    #description p {
      margin: 48px 0 40px;
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 400;
      line-height: 52px;
      color: var(--web-kiosk-text-color, hotpink);
    }

    #cards {
      display: flex;
      gap: var(--web-kiosk-dimension-spacer-m, 32px);
      margin-top: var(--web-kiosk-dimension-spacer-xxxs, 8px);
    }

    .img-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin-top: -220px;
      width: 519px;
      height: 538px;
      background: var(--web-kiosk-device-card-img-bg, hotpink);
      border-radius: 50%;
    }

    .img-container img {
      margin-top: 180px;
      max-width: 100%;
    }

    .text-container {
      margin: 0;
      padding: 22px 50px var(--web-kiosk-dimension-spacer-xxxs, 8px);
    }

    .text-container h2 {
      margin: 0;
      padding: 0;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 700;
      line-height: 38px;
    }
  `;
}
